<template>
  <div class="header-wrapper">

    <header class="site-header" :class="{ scrolled: isScrolled }">
      <div class="header-inner">

        <!-- Brand -->
        <router-link to="/" class="brand" @click.native="closeMenu">
          
          <div class="brand-text">
            <span class="brand-name">CYBER CRAFT</span>
            <span class="brand-tagline">INNOVATIONS</span>
          </div>
        </router-link>

        <!-- Desktop nav -->
        <nav class="desktop-nav">
          <router-link
            v-for="link in navLinks" :key="link.path"
            :to="link.path" class="nav-link"
            :class="{ active: $route.path === link.path }"
          >{{ link.label }}</router-link>
        </nav>

        <!-- Actions -->
        <div class="header-actions">
          <router-link to="/contactUs" class="cta-btn desktop-only">
            Start a Project
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </router-link>

          <button
            class="hamburger mobile-only"
            :class="{ open: mobileOpen }"
            @click="mobileOpen = !mobileOpen"
            aria-label="Toggle menu"
          >
            <span></span><span></span><span></span>
          </button>
        </div>

      </div>
    </header>

    <!-- Mobile dropdown -->
    <transition name="slide-down">
      <div v-if="mobileOpen" class="mobile-menu">
        <nav class="mobile-nav">
          <router-link
            v-for="link in navLinks" :key="link.path"
            :to="link.path" class="mobile-link"
            :class="{ active: $route.path === link.path }"
            @click.native="closeMenu"
          >
            <span class="ml-icon-wrap">
              <v-icon size="14" :color="$route.path === link.path ? '#5ce1e6' : 'rgba(255,255,255,0.4)'">
                {{ link.icon }}
              </v-icon>
            </span>
            <span class="ml-label">{{ link.label }}</span>
            <svg class="ml-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </router-link>
        </nav>

        <div class="mm-footer">
          <router-link to="/contactUs" class="mobile-cta" @click.native="closeMenu">
            Start a Project
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </router-link>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="mobileOpen" class="overlay" @click="closeMenu"></div>
    </transition>

  </div>
</template>

<script>
export default {
  name: 'SiteHeader',
  data() {
    return {
      mobileOpen: false,
      isScrolled: false,
      navLinks: [
        { label: 'Home',         path: '/',             icon: 'mdi-home-outline'           },
        { label: 'Services',     path: '/ourServices',  icon: 'mdi-briefcase-outline'      },
        { label: 'Works',        path: '/ourWorksHome', icon: 'mdi-layers-outline'         },
        { label: 'AI Solutions', path: '/aiPage',       icon: 'mdi-brain'                  },
        { label: 'Our Story',    path: '/ourStory',     icon: 'mdi-book-open-outline'      },
        { label: 'Team',         path: '/ourTeam',      icon: 'mdi-account-group-outline'  },
        { label: 'Careers',      path: '/careers',      icon: 'mdi-rocket-launch-outline'  },
      ]
    };
  },

  mounted() {
    // Use requestAnimationFrame-safe scroll listener
    this._onScroll = () => {
      this.isScrolled = window.scrollY > 20;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // Also watch route changes to close menu
    this.$watch(() => this.$route.path, () => this.closeMenu());
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll);
  },

  methods: {
    closeMenu() {
      this.mobileOpen = false;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── SHELL ── */
.site-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  height: 64px;
  background: transparent;
  border-bottom: 1px solid transparent;
  transition: background 0.35s ease, border-color 0.35s ease, backdrop-filter 0.35s ease;
}

/* Scrolled state — clearly visible */
.site-header.scrolled {
  background: rgba(6, 8, 14, 0.94);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-color: rgba(92, 225, 230, 0.12);
  box-shadow: 0 1px 0 rgba(92, 225, 230, 0.06);
}

/* ── INNER ── */
.header-inner {
  max-width: 1300px;
  margin: 0 auto;
  height: 64px;
  padding: 0 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

/* ── BRAND ── */
.brand {
  display: flex; align-items: center; gap: 10px;
  text-decoration: none; flex-shrink: 0;
}
.brand-icon {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(92, 225, 230, 0.07);
  border: 1px solid rgba(92, 225, 230, 0.22);
  border-radius: 9px;
  flex-shrink: 0;
  transition: background 0.22s, border-color 0.22s, box-shadow 0.22s;
}
.brand:hover .brand-icon {
  background: rgba(92, 225, 230, 0.14);
  border-color: rgba(92, 225, 230, 0.44);
  box-shadow: 0 0 14px rgba(92, 225, 230, 0.18);
}
.brand-text { display: flex; flex-direction: column; gap: 1px; }
.brand-name {
  font-family: 'Inter', sans-serif;
  font-size: 14.5px; font-weight: 700;
  color: #fff; letter-spacing: -0.3px; line-height: 1;
}
.brand-tagline {
  font-family: 'Inter', sans-serif;
  font-size: 8px; font-weight: 600;
  color: #5ce1e6; letter-spacing: 2.8px; line-height: 1;
}

/* ── DESKTOP NAV ── */
.desktop-nav {
  display: flex; align-items: center; gap: 1px;
  flex: 1; justify-content: center;
}
.nav-link {
  position: relative;
  padding: 6px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none; border-radius: 7px; white-space: nowrap;
  transition: color 0.18s ease, background 0.18s ease;
}
.nav-link::after {
  content: '';
  position: absolute; bottom: 2px; left: 12px; right: 12px;
  height: 1.5px; border-radius: 2px;
  background: linear-gradient(90deg, #5ce1e6, #4facfe);
  transform: scaleX(0); transform-origin: left;
  transition: transform 0.22s ease;
}
.nav-link:hover { color: #fff; background: rgba(255,255,255,0.05); }
.nav-link:hover::after { transform: scaleX(1); }
.nav-link.active { color: #5ce1e6; }
.nav-link.active::after { transform: scaleX(1); }

/* ── ACTIONS ── */
.header-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

.cta-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 20px;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  color: #06080e;
  background: linear-gradient(135deg, #5ce1e6, #4facfe);
  border-radius: 8px; text-decoration: none; white-space: nowrap;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.cta-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(92,225,230,0.36); }

.desktop-only { display: inline-flex; }
.mobile-only  { display: none; }

/* ── HAMBURGER ── */
.hamburger {
  display: none; flex-direction: column;
  justify-content: center; align-items: center; gap: 4px;
  width: 38px; height: 38px;
  background: rgba(92,225,230,0.06);
  border: 1px solid rgba(92,225,230,0.2);
  border-radius: 9px; cursor: pointer; padding: 0;
  transition: background 0.2s, border-color 0.2s;
}
.hamburger:hover { background: rgba(92,225,230,0.12); border-color: rgba(92,225,230,0.38); }
.hamburger span {
  display: block; width: 16px; height: 1.5px;
  background: #fff; border-radius: 2px;
  transform-origin: center;
  transition: transform 0.26s ease, opacity 0.26s ease, width 0.26s ease;
}
.hamburger.open span:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; width: 0; }
.hamburger.open span:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }

/* ══════════════════════════════════════
   MOBILE MENU — compact & professional
══════════════════════════════════════ */
.mobile-menu {
  position: fixed;
  top: 64px; left: 0; right: 0;
  z-index: 999;
  background: rgba(6, 8, 16, 0.97);
  border-bottom: 1px solid rgba(92, 225, 230, 0.1);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
}

/* Nav list */
.mobile-nav {
  padding: 8px 12px 4px;
  display: flex; flex-direction: column; gap: 1px;
}

.mobile-link {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 500;
  color: rgba(255, 255, 255, 0.68);
  text-decoration: none; border-radius: 8px;
  border: 1px solid transparent;
  transition: color 0.18s, background 0.18s, border-color 0.18s;
}
.mobile-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}
.mobile-link.active {
  color: #5ce1e6;
  background: rgba(92, 225, 230, 0.07);
  border-color: rgba(92, 225, 230, 0.18);
}

/* Small icon box */
.ml-icon-wrap {
  width: 26px; height: 26px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
}
.mobile-link.active .ml-icon-wrap {
  background: rgba(92, 225, 230, 0.09);
  border-color: rgba(92, 225, 230, 0.22);
}

.ml-label { flex: 1; }

.ml-arrow {
  color: rgba(255, 255, 255, 0.18); flex-shrink: 0;
  transition: color 0.18s, transform 0.18s;
}
.mobile-link:hover .ml-arrow  { color: rgba(255,255,255,0.42); transform: translateX(2px); }
.mobile-link.active .ml-arrow { color: rgba(92,225,230,0.55); transform: translateX(2px); }

/* Footer CTA */
.mm-footer {
  padding: 8px 12px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.mobile-cta {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px 16px;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  color: #06080e;
  background: linear-gradient(135deg, #5ce1e6, #4facfe);
  border-radius: 9px; text-decoration: none;
  transition: box-shadow 0.2s, transform 0.2s;
}
.mobile-cta:hover { box-shadow: 0 6px 22px rgba(92,225,230,0.38); transform: translateY(-1px); }

/* ── OVERLAY ── */
.overlay {
  position: fixed; inset: 0; z-index: 998;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
}

/* ── TRANSITIONS ── */
.slide-down-enter-active,
.slide-down-leave-active { transition: transform 0.26s ease, opacity 0.26s ease; }
.slide-down-enter-from,
.slide-down-leave-to     { transform: translateY(-6px); opacity: 0; }

.fade-enter-active,
.fade-leave-active { transition: opacity 0.26s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }

/* ── RESPONSIVE ── */
@media (max-width: 1100px) {
  .desktop-nav  { display: none; }
  .desktop-only { display: none !important; }
  .mobile-only  { display: flex !important; }
  .hamburger    { display: flex; }
}
@media (max-width: 960px) {
  .header-inner { padding: 0 24px; }
}
@media (max-width: 600px) {
  .header-inner  { padding: 0 14px; }
  .brand-tagline { display: none; }
  .brand-name    { font-size: 13.5px; }
  .mobile-nav    { padding: 6px 10px 2px; }
  .mm-footer     { padding: 6px 10px 12px; }
  .mobile-link   { padding: 8px 8px; font-size: 12.5px; }
  .mobile-cta    { padding: 10px 14px; font-size: 12.5px; }
}
@media (max-width: 380px) {
  .brand-icon { width: 30px; height: 30px; }
  .brand-name { font-size: 12.5px; }
}
</style>